import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import DataTable from "react-data-table-component";
import {
  Box,
  Stack,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  Avatar,
  LinearProgress,
  Skeleton,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Visibility,
  WorkOutline as JobIcon,
  SettingsOutlined,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";
import useAPI from "hooks/useAPI";
import useAlert from "hooks/useAlert";
import useConstants from "constants";
import { usePermissions } from "context/PermissionsContext";
import Unauthorized from "components/UI/Unauthorized";
import AlertMessage from "components/UI/AlertMessage";
import SoftButton from "components/SoftButton";
import MBDrawer from "components/MBDrawer";
import PageSettings from "components/PageSettings";
import SortIcon from "components/SortIcon";
import WithPermission from "components/WithPermission";
import { formatDateWithFormat } from "utils/utilities";

const SEARCH_DEBOUNCE_MS = 400;
const ALL_FILTER = "__all__";

// ─── Status chip ──────────────────────────────────────────────────────────────

const STATUS_META = {
  completed: { color: "#22c55e", bg: "rgba(34,197,94,0.10)", label: "Completed" },
  failed: { color: "#ef4444", bg: "rgba(239,68,68,0.10)", label: "Failed" },
  running: { color: "#3b82f6", bg: "rgba(59,130,246,0.10)", label: "Running" },
  pending: { color: "#f59e0b", bg: "rgba(245,158,11,0.10)", label: "Pending" },
  cancelled: { color: "#6b7280", bg: "rgba(107,114,128,0.10)", label: "Cancelled" },
  processing: { color: "#8b5cf6", bg: "rgba(139,92,246,0.10)", label: "Processing" },
};

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || { color: "#9ca3af", bg: "rgba(0,0,0,0.06)", label: status };
  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{
        background: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.color}44`,
        fontWeight: 700,
        fontSize: "0.7rem",
        height: 22,
      }}
    />
  );
};

StatusChip.propTypes = { status: PropTypes.string };

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toTitleCase = (str) => {
  if (!str) return "";
  return str
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

// ─── Expanded row ─────────────────────────────────────────────────────────────

const ExpandedRow = ({ data }) => {
  const formatPayload = (payload) => {
    if (!payload) return "No payload available";
    try {
      const parsed = typeof payload === "string" ? JSON.parse(payload) : payload;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return typeof payload === "string" ? payload : JSON.stringify(payload, null, 2);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          background: "rgba(0,0,0,0.02)",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          {t("lang.details") || "Payload Details"}
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            backgroundColor: "background.paper",
            borderRadius: 1.5,
            overflow: "auto",
            fontSize: "0.8rem",
            fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: "300px",
            border: "1px solid",
            borderColor: "divider",
            color: "text.primary",
          }}
        >
          {formatPayload(data.raw_payload)}
        </Box>
      </Paper>
    </Box>
  );
};

ExpandedRow.propTypes = {
  data: PropTypes.object.isRequired,
};

// ─── Main component ───────────────────────────────────────────────────────────

const Jobs = () => {
  const { api } = useAPI();
  const { errorState, showAlert } = useAlert();
  const { per_page, items_per_page_options, date_time_format } = useConstants();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const table_id = "jobs";
  const default_sort_column = "id";
  const default_sort_direction = "desc";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoaded, setSearchLoaded] = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(per_page);
  const [searchInput, setSearchInput] = useState("");
  const [orderColumn, setOrderColumn] = useState(default_sort_column);
  const [orderDirection, setOrderDirection] = useState(default_sort_direction);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Job name filter
  const [jobNames, setJobNames] = useState([]);
  const [selectedJobName, setSelectedJobName] = useState(ALL_FILTER);

  // Settings drawer
  const [openSettingsDrawer, setOpenSettingsDrawer] = useState(false);
  const [tableId, setTableId] = useState(null);
  const [settingsSaveBtnLoading, setSettingsSaveBtnLoading] = useState(false);
  const [defaultSortColumnFromSettings, setDefaultSortColumnFromSettings] =
    useState(default_sort_column);
  const [defaultSortDirectionFromSettings, setDefaultSortDirectionFromSettings] =
    useState(default_sort_direction);
  const [defaultItemsPerPageFromSettings, setDefaultItemsPerPageFromSettings] = useState(per_page);

  if (!hasPermission("can_view_gbwm")) {
    return <Unauthorized />;
  }

  // ── Fetch job names once on mount ──────────────────────────────────────────
  useEffect(() => {
    api
      .get("/job-names/")
      .then((res) => setJobNames(res.data?.data || []))
      .catch(() => {});
  }, []);

  // ── Fetch jobs ─────────────────────────────────────────────────────────────
  const fetchJobs = useCallback(
    async (sortCol, sortDir, pageSize, isReset, searchValue, nextPage, jobNameOverride) => {
      setLoading(true);
      const resolvedSortCol = sortCol || orderColumn;
      const resolvedSortDir = sortDir || orderDirection;
      const resolvedPerPage = pageSize || perPage;
      const resolvedPage = nextPage || page;
      const nameFilter = jobNameOverride !== undefined ? jobNameOverride : selectedJobName;

      let requestConfig = {};
      if (!isInitialLoad && !isReset) {
        const params = {
          page: resolvedPage,
          per_page: resolvedPerPage,
          search: searchValue,
          order_col: resolvedSortCol,
          order_dir: resolvedSortDir,
        };
        if (nameFilter && nameFilter !== ALL_FILTER) params.name = nameFilter;
        requestConfig = { params };
      } else {
        const params = {};
        if (nameFilter && nameFilter !== ALL_FILTER) params.name = nameFilter;
        if (Object.keys(params).length) requestConfig = { params };
      }

      try {
        const response = await api.get("/jobs/", requestConfig);
        const result = response.data;
        const ps = result.page_settings;
        setData(result.data || []);
        setTotalRows(result.recordsTotal || 0);

        if (isInitialLoad || isReset) {
          if (ps?.sortColumn) {
            setOrderColumn(ps.sortColumn);
            setDefaultSortColumnFromSettings(ps.sortColumn);
          }
          if (ps?.sortDirection) {
            setOrderDirection(ps.sortDirection);
            setDefaultSortDirectionFromSettings(ps.sortDirection);
          }
          if (ps?.itemsPerPage) {
            setPerPage(ps.itemsPerPage);
            setDefaultItemsPerPageFromSettings(ps.itemsPerPage);
          }
          if (isReset && (!ps || Object.keys(ps).length === 0)) {
            setOrderColumn(default_sort_column);
            setOrderDirection(default_sort_direction);
            setPerPage(per_page);
            setDefaultSortColumnFromSettings(default_sort_column);
            setDefaultSortDirectionFromSettings(default_sort_direction);
            setDefaultItemsPerPageFromSettings(per_page);
          }
          setIsInitialLoad(false);
        }
        setSearchLoaded(true);
      } catch (err) {
        showAlert("error", err?.response?.data?.error || t("lang.something_went_wrong"));
      } finally {
        setLoading(false);
      }
    },
    [api, orderColumn, orderDirection, perPage, page, isInitialLoad, selectedJobName]
  );

  const isFirstSearchEffect = useRef(true);
  const suppressDebounceOnce = useRef(false);

  useEffect(() => {
    fetchJobs(orderColumn, orderDirection, perPage, false, searchInput, page, selectedJobName);
  }, []);

  // Debounced search
  useEffect(() => {
    if (suppressDebounceOnce.current) {
      suppressDebounceOnce.current = false;
      return;
    }
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setPage(1);
      fetchJobs(orderColumn, orderDirection, perPage, false, searchInput, 1, selectedJobName);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleJobNameFilter = (name) => {
    setSelectedJobName(name);
    setPage(1);
    fetchJobs(orderColumn, orderDirection, perPage, false, searchInput, 1, name);
  };

  const handlePageChange = (p) => {
    setPage(p);
    fetchJobs(orderColumn, orderDirection, perPage, false, searchInput, p, selectedJobName);
  };

  const handlePerPageChange = (pp) => {
    setPerPage(pp);
    setPage(1);
    fetchJobs(orderColumn, orderDirection, pp, false, searchInput, 1, selectedJobName);
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSort = (col, dir) => {
    const nc = col.columnKey;
    const nd = nc === orderColumn ? (orderDirection === "asc" ? "desc" : "asc") : dir;
    setOrderColumn(nc);
    setOrderDirection(nd);
    fetchJobs(nc, nd, perPage, false, searchInput, page, selectedJobName);
  };

  const handleReset = () => {
    if (searchInput.trim() !== "") suppressDebounceOnce.current = true;
    setSearchInput("");
    setSelectedJobName(ALL_FILTER);
    fetchJobs(orderColumn, orderDirection, perPage, true, "", 1, ALL_FILTER);
  };

  const getSortIcon = (key) => (
    <SortIcon orderDirection={orderDirection} isActive={orderColumn === key} />
  );

  // ── Settings ───────────────────────────────────────────────────────────────
  const handleSettingsClick = (tid) => {
    setTableId(tid);
    setOpenSettingsDrawer(true);
  };

  const handleSettingsChange = async (settings) => {
    const ns = settings[tableId];
    setSettingsSaveBtnLoading(true);
    try {
      const res = await api.post("/user-page-settings/", { page_slug: "jobs", settings });
      if (res.status === 200) {
        setOrderColumn(ns.sortColumn);
        setOrderDirection(ns.sortDirection);
        setPerPage(ns.itemsPerPage);
        setDefaultSortColumnFromSettings(ns.sortColumn);
        setDefaultSortDirectionFromSettings(ns.sortDirection);
        setDefaultItemsPerPageFromSettings(ns.itemsPerPage);
        setPage(1);
        fetchJobs(
          ns.sortColumn,
          ns.sortDirection,
          ns.itemsPerPage,
          false,
          searchInput,
          1,
          selectedJobName
        );
        setOpenSettingsDrawer(false);
        setTableId(null);
        showAlert("success", res.data.message);
      } else {
        showAlert("error", res.data.error);
      }
    } catch (err) {
      showAlert("error", err?.response?.data?.error || "");
    } finally {
      setSettingsSaveBtnLoading(false);
    }
  };

  // ── Columns ────────────────────────────────────────────────────────────────
  const colHeader = (label, key) => (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      sx={{ textTransform: "uppercase", fontSize: "0.72rem", fontWeight: 700 }}
    >
      {label}
      {getSortIcon(key)}
    </Stack>
  );

  const columns = [
    {
      name: colHeader(t("lang.hash_id") || "#ID", "id"),
      columnKey: "id",
      selector: (row) => (
        <Typography variant="body2" fontWeight={600} color="text.primary">
          #{row.id}
        </Typography>
      ),
      sortable: true,
      width: "90px",
    },
    {
      name: colHeader(t("lang.name") || "Name", "name"),
      columnKey: "name",
      selector: (row) => (
        <Typography variant="caption" fontWeight={600} color="text.primary">
          {toTitleCase(row.name)}
        </Typography>
      ),
      sortable: true,
      width: "160px",
    },
    {
      name: colHeader(t("lang.details") || "Details", "payload"),
      columnKey: "payload",
      selector: (row) => (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            py: 0.5,
          }}
        >
          {row.payload}
        </Typography>
      ),
      sortable: true,
      grow: 1,
    },
    {
      name: colHeader(t("lang.date") || "Date", "modified_at"),
      columnKey: "modified_at",
      selector: (row) => (
        <Typography variant="caption" color="text.primary" fontWeight={500}>
          {formatDateWithFormat(row.modified_at, date_time_format)}
        </Typography>
      ),
      sortable: true,
      width: "170px",
    },
    {
      name: colHeader(t("lang.status") || "Status", "status"),
      columnKey: "status",
      selector: (row) => <StatusChip status={row.status} />,
      sortable: true,
      width: "120px",
    },
  ];

  if (hasPermission("can_view_gbwm")) {
    columns.push({
      name: (
        <Typography sx={{ textTransform: "uppercase", fontSize: "0.72rem", fontWeight: 700 }}>
          {t("lang.actions.action") || "Actions"}
        </Typography>
      ),
      selector: (row) =>
        !row.is_default ? (
          <Stack direction="row" alignItems="center">
            <WithPermission permission="can_view_gbwm">
              <Tooltip title={t("lang.view_job") || "View Job"} placement="top">
                <IconButton size="small" onClick={() => navigate(`/job/${row.id}`)}>
                  <Visibility fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            </WithPermission>
          </Stack>
        ) : null,
      sortable: false,
      width: "80px",
    });
  }

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "rgba(0,0,0,0.025)",
        borderBottomWidth: "2px",
        borderBottomColor: "rgba(0,0,0,0.08)",
        minHeight: "44px",
      },
    },
    headCells: {
      style: {
        paddingLeft: "12px",
        paddingRight: "8px",
        fontWeight: 700,
        fontSize: "0.72rem",
        color: "#64748b",
        letterSpacing: "0.04em",
      },
    },
    rows: {
      style: {
        minHeight: "52px",
        borderBottomColor: "rgba(0,0,0,0.05) !important",
        "&:hover": { backgroundColor: "rgba(59,130,246,0.04)" },
      },
    },
    cells: {
      style: { paddingLeft: "12px", paddingRight: "8px" },
    },
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Job name filter chips */}
      {jobNames.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: "10px 16px",
            borderRadius: 2.5,
            border: "1.5px solid",
            borderColor: "divider",
            background:
              "linear-gradient(135deg, rgba(99,102,241,0.05) 0%, rgba(79,70,229,0.03) 100%)",
          }}
        >
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Stack direction="row" alignItems="center" gap={0.5} mr={0.5}>
              <FilterListIcon sx={{ fontSize: "0.95rem", color: "text.secondary" }} />
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
              >
                {t("lang.filter") || "Filter"}
              </Typography>
            </Stack>
            <Chip
              label="All"
              size="small"
              onClick={() => handleJobNameFilter(ALL_FILTER)}
              variant={selectedJobName === ALL_FILTER ? "filled" : "outlined"}
              sx={{
                fontWeight: 700,
                fontSize: "0.72rem",
                cursor: "pointer",
                ...(selectedJobName === ALL_FILTER
                  ? {
                      background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                      color: "#fff",
                      border: "none",
                    }
                  : { borderColor: "#6366f133", color: "text.secondary" }),
              }}
            />
            {jobNames.map((name) => (
              <Chip
                key={name}
                label={toTitleCase(name)}
                size="small"
                onClick={() => handleJobNameFilter(name)}
                variant={selectedJobName === name ? "filled" : "outlined"}
                sx={{
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  cursor: "pointer",
                  ...(selectedJobName === name
                    ? {
                        background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                        color: "#fff",
                        border: "none",
                      }
                    : { borderColor: "#6366f133", color: "text.secondary" }),
                }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {/* Toolbar */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={2}
        flexWrap="wrap"
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              background: "linear-gradient(135deg, #6366f1, #4f46e5)",
              fontSize: "0.85rem",
            }}
          >
            <JobIcon sx={{ fontSize: "1rem" }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1}>
              {selectedJobName === ALL_FILTER
                ? t("lang.jobs") || "Jobs"
                : toTitleCase(selectedJobName)}
            </Typography>
            {searchLoaded && (
              <Typography variant="caption" color="text.secondary">
                {totalRows} {totalRows !== 1 ? "records" : "record"}
              </Typography>
            )}
          </Box>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center">
          <Tooltip title={t("lang.settings") || "Settings"} arrow>
            <IconButton
              onClick={() => handleSettingsClick(table_id)}
              sx={{ border: "1.5px solid", borderColor: "divider", borderRadius: 2, p: 0.75 }}
            >
              <SettingsOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("lang.reset") || "Refresh"} arrow>
            <IconButton
              onClick={handleReset}
              sx={{ border: "1.5px solid", borderColor: "divider", borderRadius: 2, p: 0.75 }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            size="small"
            placeholder={t("lang.actions.search") || "Search records…"}
            value={searchInput}
            onChange={handleSearchChange}
            sx={{
              width: 220,
              "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.875rem" },
            }}
          />
        </Stack>
      </Stack>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: "1.5px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <DataTable
          className={table_id}
          key={`${orderColumn}-${orderDirection}-${perPage}-${data.length}`}
          columns={columns}
          data={data}
          progressPending={loading}
          progressComponent={
            <Box sx={{ width: "100%", py: 4 }}>
              <LinearProgress sx={{ mx: 3, borderRadius: 3, mb: 2 }} />
              {[...Array(5)].map((_, i) => (
                <Stack key={i} direction="row" gap={2} px={3} py={0.75}>
                  <Skeleton width={60} height={20} />
                  <Skeleton width="40%" height={20} />
                  <Skeleton width={120} height={20} />
                  <Skeleton width={80} height={20} />
                </Stack>
              ))}
            </Box>
          }
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={handlePerPageChange}
          onChangePage={handlePageChange}
          onSort={handleSort}
          sortServer
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={items_per_page_options}
          paginationDefaultPage={page}
          clearSelectedRows
          responsive
          selectableRows={false}
          pointerOnHover
          fixedHeader
          persistTableHead
          expandableRows
          expandableRowsComponent={({ data: rowData }) => <ExpandedRow data={rowData} />}
          sortIcon={() => <></>}
          customStyles={customStyles}
          highlightOnHover
          noDataComponent={
            <Box sx={{ py: 8, textAlign: "center" }}>
              <JobIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography color="text.secondary">
                {t("lang.no_records_found") || "No records found."}
              </Typography>
            </Box>
          }
        />
      </Paper>

      <AlertMessage {...errorState} />

      {/* Settings drawer */}
      <MBDrawer
        open={openSettingsDrawer}
        onClose={() => setOpenSettingsDrawer(false)}
        title={t("lang.settings") || "Settings"}
        anchor="right"
        height="100vh"
        width="400px"
      >
        <PageSettings
          saveBtnLoading={settingsSaveBtnLoading}
          currentSortColumn={orderColumn}
          currentSortDirection={orderDirection}
          currentItemsPerPage={perPage}
          tableId={tableId}
          defaultSortColumn={defaultSortColumnFromSettings}
          defaultSortDirection={defaultSortDirectionFromSettings}
          defaultItemsPerPage={defaultItemsPerPageFromSettings}
          sortableColumns={[
            { value: "id", label: t("lang.id") || "ID" },
            { value: "name", label: t("lang.name") || "Name" },
            { value: "status", label: t("lang.status") || "Status" },
            { value: "modified_at", label: t("lang.date") || "Date" },
          ]}
          onSettingsChange={handleSettingsChange}
        />
      </MBDrawer>
    </Box>
  );
};

export default Jobs;
